/**
 * IsolatedOptionsTable container sagas
 *
 * @author Diogo Guedes <dguedes@ubiwhere.com>
 *
 */

import { call, takeLatest, put, putResolve, select } from 'redux-saga/effects';

import { PayloadAction } from '@reduxjs/toolkit';

import { t } from 'app';

import API from 'api';

import { actions } from 'store/rootSlices';

import * as selectors from './selectors';

import ErrorHandler from 'shared/errorHandler';

import {
  ICallParams,
  IRegistration,
  DEFAULT_PAGINATION_PARAMS,
  IIsolatedOptionsPayload,
} from '../types';


function* onMountSaga(action: PayloadAction<IRegistration & { mandatoryFilters?: boolean }>) {
  try {
    yield put(
      actions.IsolatedOptionsTable.setLoadingStatus({ fieldName: 'loading', fieldValue: true })
    );
    
    yield putResolve(
      actions.IsolatedOptionsTable.fetchIsolatedOptionsFilters({
        ...DEFAULT_PAGINATION_PARAMS,
        registrationId: action.payload.registrationId,
        mandatoryFilters: action.payload.mandatoryFilters,
      })
    );
  } catch (e) {
    const shouldRun = yield call(ErrorHandler, e);
    if (shouldRun) {
      yield put(
        actions.Toaster.showToaster({
          title: t('sgh.actionErrorGetFreeOptionsList', { textOnly: true }),
          icon: 'error',
          type: 'danger',
        })
      );
    }
  } 
}

function* fetchIsolatedOptionsSaga(
  action: PayloadAction<ICallParams & IRegistration & { mandatoryFilters?: boolean }>
) {
  try {
    yield put(
      actions.IsolatedOptionsTable.setLoadingStatus({ fieldName: 'loadingTable', fieldValue: true })
    );

    let isolatedOptionsListSlice = yield select(selectors.getIsolatedOptionsListSlice);
    let isolatedOptionsData: IIsolatedOptionsPayload;

    if (action.payload.mandatoryFilters) {
      // refreshes filters if at least one filter is empty
      isolatedOptionsListSlice = {
        ...isolatedOptionsListSlice,
        filterParams: { ...isolatedOptionsListSlice.filterParams },
      };
    }


    isolatedOptionsData = yield call(API.sgh.getIsolatedOptions.call, {
      ...action.payload,
      ...isolatedOptionsListSlice.filterParams,
    });

    
    yield put(actions.IsolatedOptionsTable.setIsolatedOptionsData(isolatedOptionsData));

  } catch (e) {
    yield put(
      actions.IsolatedOptionsTable.setErrorStatus({
        fieldName: 'errLoadingIsolatedOptions',
        fieldValue: true,
      })
    );

    const shouldRun = yield call(ErrorHandler, e);
    if (shouldRun) {
      yield put(
        actions.Toaster.showToaster({
          title: t('secVirtualNotifications.isolatedOptionsList_errorLoadingPage'),
          icon: 'error',
          type: 'danger',
        })
      );
    }
  } finally {
    yield put(
      actions.IsolatedOptionsTable.setLoadingStatus({ fieldName: 'loading', fieldValue: false })
    );
    yield put(
      actions.IsolatedOptionsTable.setLoadingStatus({ fieldName: 'loadingTable', fieldValue: false })
    );
  }
}

function* fetchIsolatedOptionsFiltersSaga(
  action: PayloadAction<ICallParams & IRegistration & { mandatoryFilters?: boolean }>
) {

  try {
    
    let isolatedOptionsListSlice = yield select(selectors.getIsolatedOptionsListSlice);
    let isolatedOptionsData: IIsolatedOptionsPayload;

    if (action.payload.mandatoryFilters) {
      
      // refreshes filters if at least one filter is empty
      isolatedOptionsListSlice = {
        ...isolatedOptionsListSlice,
        filterParams: { ...isolatedOptionsListSlice.filterParams, onlyFilters: true },
      };
    }

    isolatedOptionsData = yield call(API.sgh.getIsolatedOptions.call, {
      ...isolatedOptionsListSlice.filterParams,
    });

    

    yield put(actions.IsolatedOptionsTable.setIsolatedOptionsData(isolatedOptionsData));

  } catch (e) {
    yield put(
      actions.IsolatedOptionsTable.setErrorStatus({
        fieldName: 'errLoadingIsolatedOptions',
        fieldValue: true,
      })
    );

    const shouldRun = yield call(ErrorHandler, e);
    if (shouldRun) {
      yield put(
        actions.Toaster.showToaster({
          title: t('secVirtualNotifications.isolatedOptionsList_errorLoadingPage'),
          icon: 'error',
          type: 'danger',
        })
      );
    }
  }  finally {
    yield put(
      actions.IsolatedOptionsTable.setLoadingStatus({ fieldName: 'loading', fieldValue: false })
    );
    yield put(
      actions.IsolatedOptionsTable.setLoadingStatus({ fieldName: 'loadingTable', fieldValue: false })
    );
  }
}

function* onUnmountSaga() {}

export default function* watcherSignin() {
  yield takeLatest('IsolatedOptionsTable/onMount', onMountSaga);
  yield takeLatest('IsolatedOptionsTable/fetchIsolatedOptions', fetchIsolatedOptionsSaga);
  yield takeLatest('IsolatedOptionsTable/fetchIsolatedOptionsFilters', fetchIsolatedOptionsFiltersSaga);
  yield takeLatest('IsolatedOptionsTable/onUnmount', onUnmountSaga);
}
